import { memo, useCallback, type ReactNode } from 'react';
import type { SectionDraft } from '../builder/schema-draft';

export interface WysiwygSectionShellProps {
  section: SectionDraft;
  selected: boolean;
  defaultNode: ReactNode;
  onSelect: (uiId: string | null) => void;
}

/**
 * Selection wrapper around a rendered section. Like WysiwygFieldShell, this
 * never modifies the rendered output — it just adds an outline on hover and
 * when selected, and routes clicks on the rendered `.dynamic-form-section-header`
 * (the dark blue title bar) to selecting the section.
 *
 * All editing happens in the side-panel inspector, never inline.
 */
export const WysiwygSectionShell = memo(function WysiwygSectionShell({
  section,
  selected,
  defaultNode,
  onSelect,
}: WysiwygSectionShellProps) {
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (selected) return;
      const target = e.target as HTMLElement;
      // Field clicks are owned by the field shell — let them through.
      if (target.closest('.wysiwyg-field-shell')) return;
      // Only the section's title bar selects the section. Clicks on body
      // gaps (between fields) deselect via the canvas handler.
      if (target.closest('.dynamic-form-section-header')) {
        e.stopPropagation();
        onSelect(section.uiId);
      }
    },
    [selected, onSelect, section.uiId],
  );

  return (
    <div
      className={'wysiwyg-section-shell' + (selected ? ' wysiwyg-section-shell--selected' : '')}
      onMouseDown={handleMouseDown}
      data-ui-id={section.uiId}
    >
      {defaultNode}
    </div>
  );
});
